import ChargeHelper, { ChargeConstants } from "features/Charge/chargeHelper";
import { AppDialogHelper } from "general/components/AppDialog";
import AppColors from "general/constants/AppColors";
import AppConfigs from "general/constants/AppConfigs";
import AppConstants from "general/constants/AppConstants";
import AppResource from "general/constants/AppResource";
import AppRoute from "general/constants/AppRoute";
import ToastHelper from "general/helpers/ToastHelper";
import UIHelper from "general/helpers/UIHelper";
import Utils from "general/utils/Utils";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

function PushNotificationListView({ setIsShowNotificationList = () => { } }) {
    const { user } = useSelector((state) => state.auth);
    const [activeTab, setActiveTab] = useState("ALL");

    const [notificationsByTab, setNotificationsByTab] = useState({});
    const [totalByTab, setTotalByTab] = useState({});
    const [pageByTab, setPageByTab] = useState({});
    const [hasMoreByTab, setHasMoreByTab] = useState({});

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [notificationSummary, setNotificationSummary] = useState({});

    const listRef = useRef(null);

    const tabs = useMemo(() => [
        { key: "ALL", label: "Tất cả" },
        { key: ChargeConstants.NotificationCategory.BUSINESS, label: "Kinh doanh" },
        { key: ChargeConstants.NotificationCategory.OPERATION, label: "Vận hành" },
        { key: ChargeConstants.NotificationCategory.PROMOTION, label: "Khuyến mãi" },
        { key: ChargeConstants.NotificationCategory.OTHER, label: "Khác" },
    ], []);

    const currentNotifications = notificationsByTab[activeTab] || [];
    const currentTotal = totalByTab[activeTab] || 0;
    const currentPage = pageByTab[activeTab] || 0;
    const currentHasMore = hasMoreByTab[activeTab] !== false;

    const handleGetNotifications = useCallback(async (tabKey, page = 0, append = false) => {
        if (page === 0) setLoading(true);
        else setLoadingMore(true);

        try {
            const params = {
                page,
                limit: AppConfigs.defaultPageLimit,
                isFilterByDate: false,
                isSent: 1,
            };

            if (tabKey !== "ALL") {
                params.category = tabKey;
            }

            if (user?.role === AppConstants.userRole.admin) {
                params.userId = user?.userId;
            }

            const response = await ChargeHelper.API.Notification.requestGetNotificationList(params);
            const newNotifications = response?.notifications || [];
            const newTotal = response?.total || 0;

            setTotalByTab(prev => ({ ...prev, [tabKey]: newTotal }));

            if (append) {
                setNotificationsByTab(prev => ({
                    ...prev,
                    [tabKey]: [...(prev[tabKey] || []), ...newNotifications]
                }));
            } else {
                setNotificationsByTab(prev => ({
                    ...prev,
                    [tabKey]: newNotifications
                }));
            }

            const hasMore = (page + 1) * AppConfigs.defaultPageLimit < newTotal;
            setHasMoreByTab(prev => ({ ...prev, [tabKey]: hasMore }));
            setPageByTab(prev => ({ ...prev, [tabKey]: page }));

        } catch (error) {
            console.error("Lỗi khi lấy thông báo:", error);
            if (!append) {
                setNotificationsByTab(prev => ({ ...prev, [tabKey]: [] }));
                setTotalByTab(prev => ({ ...prev, [tabKey]: 0 }));
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [user?.role, user?.userId]);

    useEffect(() => {
        setNotificationsByTab(prev => ({ ...prev, [activeTab]: [] }));
        setPageByTab(prev => ({ ...prev, [activeTab]: 0 }));
        setHasMoreByTab(prev => ({ ...prev, [activeTab]: true }));
        handleGetNotifications(activeTab, 0, false);

        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [activeTab, handleGetNotifications]);

    const handleScroll = useCallback(() => {
        if (!listRef.current || loadingMore || !currentHasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        if (scrollHeight - scrollTop - clientHeight < 100) {
            const nextPage = currentPage + 1;
            setPageByTab(prev => ({ ...prev, [activeTab]: nextPage }));
            handleGetNotifications(activeTab, nextPage, true);
        }
    }, [loadingMore, currentHasMore, currentPage, activeTab, handleGetNotifications]);

    const handleGetNotificationSummary = useCallback(async () => {
        try {
            const params = { isRead: 0, isSent: 1 };
            if (user?.role === AppConstants.userRole.admin) {
                params.userId = user?.userId;
            }
            const categories = await ChargeHelper.API.Notification.requestGetNotificationSummary(params);
            setNotificationSummary(categories || {});
        } catch (error) {
            console.error("Lỗi khi lấy thống kê thông báo:", error);
        }
    }, [user?.role, user?.userId]);

    const handleReadNotification = useCallback(async (item) => {
        if (item?.isRead) return;

        try {
            const res = await ChargeHelper.API.Notification.requestUpdateNotification(item?.notificationId, { isRead: 1 });

            if (res === true) {
                setNotificationsByTab(prev => ({
                    ...prev,
                    [activeTab]: prev[activeTab].map(noti =>
                        noti?.notificationId === item?.notificationId ? { ...noti, isRead: 1 } : noti
                    )
                }));

                if (item?.category in notificationSummary) {
                    setNotificationSummary(prev => ({
                        ...prev,
                        [item.category]: Math.max(0, (prev[item.category] || 0) - 1)
                    }));
                }
            } else if (typeof res === "string") {
                ToastHelper.showError(res);
            }
        } catch (error) {
            ToastHelper.showError("Không thể đánh dấu thông báo đã đọc");
        }
    }, [activeTab, notificationSummary]);

    useEffect(() => {
        handleGetNotificationSummary();
    }, [handleGetNotificationSummary]);

    useEffect(() => {
        const element = listRef.current;
        if (!element) return;

        element.addEventListener("scroll", handleScroll);
        element.addEventListener("wheel", handleScroll, { passive: true });
        element.addEventListener("touchmove", handleScroll, { passive: true });

        return () => {
            element.removeEventListener("scroll", handleScroll);
            element.removeEventListener("wheel", handleScroll);
            element.removeEventListener("touchmove", handleScroll);
        };
    }, [handleScroll]);

    const handleReadAllNotification = async () => {
        AppDialogHelper.show(
            `Đọc tất cả`,
            `Bạn có muốn đánh dấu tất cả các thông báo là đã đọc?`,
            [
                {
                    title: 'Hủy bỏ',
                    type: 'neutral',
                    onClick: () => {
                        AppDialogHelper.hide();
                    },
                },
                {
                    title: 'Xác nhận',
                    type: 'success',
                    onClick: async () => {
                        AppDialogHelper.hide();
                        const params = {};

                        if (user?.role === AppConstants.userRole.admin) {
                            params.userId = user?.userId;
                        }
                        const isSuccess = await ChargeHelper.API.Notification.requestReadAllNotification(params);;
                        if (isSuccess) {
                            ToastHelper.showSuccess('Đã đánh dấu tất cả thông báo là đã đọc');
                        } else {
                            ToastHelper.showError(`Đọc tất cả thông báo thất bại`);
                        }
                    },
                },
            ],
            false,
            AppResource.images.dialogIcons.dialogReadAllNotification
        );
    }

    const detailNotificationView = (item) => {
        return (
            <div className="d-flex flex-column gap-2">
                {item?.title && (
                    <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>
                        {item?.title}
                    </span>
                )}
                {item?.message && (
                    <span
                        style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[800])}
                        dangerouslySetInnerHTML={{ __html: item?.message }}
                    />
                )}
                {item?.releaseDate && (
                    <span style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[500])}>
                        {Utils.formatDateTimeToString(item?.releaseDate, 'DD/MM/YYYY - hh:mm')}
                    </span>
                )}
            </div>
        )
    }

    const handleShowNotification = (item) => {
        AppDialogHelper.show(
            '',
            '',
            [
                {
                    title: 'Đóng lại',
                    type: 'neutral',
                    onClick: () => {
                        AppDialogHelper.hide();
                    },
                },
            ],
            false,
            null,
            detailNotificationView(item)
        );
    }

    return (
        <div className="d-flex flex-column h-100">
            <div className="px-3 py-4 border-bottom">
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <div style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>Thông báo</div>
                        <div>
                            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>Tổng số: </span>
                            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>{Utils.formatNumber(currentTotal)} thông báo</span>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-icon btn-sm btn-light rounded" onClick={() => handleReadAllNotification()}>
                            <i className="far fa-check-double"></i>
                        </button>
                        <button className="btn btn-icon btn-sm btn-light rounded" onClick={() => setIsShowNotificationList(false)}>
                            <i className="ki ki-close icon-nm"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div className="border-bottom">
                <div className="d-flex align-items-center overflow-x-auto" style={{ marginBottom: "-1px", msOverflowStyle: "none", scrollbarWidth: "none" }}>
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            className={`py-4 px-5 border-0 bg-transparent position-relative font-weight-medium text-nowrap`}
                            style={{
                                ...UIHelper.buildFontStyle(activeTab === tab.key ? "600" : "400", "14px", activeTab === tab.key ? AppColors.primary : AppColors.gray[700]),
                                transition: "all 0.2s",
                            }}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <div className="d-flex align-items-center">
                                {tab.label}
                                {notificationSummary[tab.key] > 0 && (
                                    <span className="p-1 ml-1 rounded-pill" style={{
                                        ...UIHelper.buildFontStyle("500", "10px", '#FFFFFF'),
                                        backgroundColor: AppColors.danger
                                    }}>
                                        {notificationSummary[tab.key]}
                                    </span>
                                )}
                            </div>
                            {activeTab === tab.key && (
                                <span className="position-absolute start-0 end-0 bottom-0" style={{ height: "3px", backgroundColor: AppColors.primary, borderRadius: "2px" }} />
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div ref={listRef} className="flex-grow-1 overflow-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
                {loading && currentNotifications.length === 0 ? (
                    <div className="text-center py-12">
                        <span className="spinner spinner-primary spinner-lg"></span>
                    </div>
                ) : currentNotifications.length === 0 ? (
                    <div className="text-center text-muted py-12">
                        {UIHelper.buildEmptyNoData('Không có thông báo nào.')}
                    </div>
                ) : (
                    <div className="d-flex flex-column">
                        {currentNotifications.map((item, index) => (
                            <div
                                key={`${item?.notificationId}-${index}`}
                                className="d-flex p-4 rounded bg-hover-light border-bottom gap-3 cursor-pointer"
                                style={{ borderColor: AppColors.gray[200] }}
                                onClick={() => {
                                    if (!item?.isRead) handleReadNotification(item);
                                    if (!!item?.metadata?.link) {
                                        Utils.openInNewTab(Utils.getFullUrl(item?.metadata?.link));
                                    } else {
                                        handleShowNotification(item);
                                    }
                                }}
                            >
                                <div className="p-4 rounded-lg d-flex align-items-center justify-content-center" style={{ backgroundColor: '#F3F6F9', width: '40px', height: '40px', flexShrink: 0 }}>
                                    <i className="fas fa-bell text-success" style={{ fontSize: 18 }}></i>
                                </div>
                                <div className="flex-grow-1">
                                    <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>{item?.title}</div>
                                    <div style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[800])}>{item?.message}</div>
                                    <div className="d-flex justify-content-between" style={UIHelper.buildFontStyle('500', '12px', AppColors.gray[500])}>
                                        <div>{Utils.formatDateTimeToString(item?.releaseDate, 'DD/MM/YYYY - hh:mm')}</div>
                                        <div>
                                            {item?.isRead ? (
                                                <>
                                                    <span className="mr-2">Đã xem</span>
                                                    <i className="far fa-check-double text-success" style={{ fontSize: 14 }}></i>
                                                </>
                                            ) : <></>}
                                        </div>
                                    </div>
                                </div>
                                {!item?.isRead && (
                                    <div className="align-self-start ms-3">
                                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: AppColors.danger, flexShrink: 0 }}></div>
                                    </div>
                                )}
                            </div>
                        ))}

                        {loadingMore && (
                            <div className="text-center py-4">
                                <span className="spinner spinner-primary"></span>
                            </div>
                        )}

                        {!currentHasMore && currentNotifications.length > 0 && (
                            <div className="text-center text-muted py-4 font-size-sm">
                                Đã tải hết thông báo
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="border-top text-center p-4">
                <button
                    className="btn btn-light-primary btn-sm font-weight-bold w-100"
                    style={{ ...UIHelper.buildFontStyle('600', '13px', AppColors.gray[800]), backgroundColor: AppColors.gray[200] }}
                    onClick={() => Utils.openInCurrentTab(AppRoute.systemLogList)}
                >
                    Xem tất cả thông báo
                </button>
            </div>
        </div>
    );
}

export default PushNotificationListView;